import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  EnrichedEntryStatus,
  MakerFlag
} from '../../types/entry.types';

/**
 * Per-input save affordance shared by Threshold / Size / MaxV cells.
 * Renders nothing when the field is clean and not mid-save so the
 * cell stays compact at rest. Each input gets its own copy so the
 * operator sees the tick next to the exact field they edited — a
 * single trailing tick at the end of the parameters block (the old
 * layout) was too easy to miss when editing Threshold or Size.
 *
 * The spinner only shows on fields that were dirty at save time, so
 * a single PATCH doesn't flash three identical spinners across the
 * row.
 */
export function renderSaveIndicator(
  saving: boolean,
  dirty: boolean,
  onClick: () => void,
): JSX.Element | null {
  if (saving && dirty) return <CircularProgress size={12} sx={{ ml: 0.2 }} />;
  if (saving) return null;
  if (!dirty) return null;
  return (
    <IconButton
      size="small"
      onClick={onClick}
      aria-label="save"
      sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
    >
      <CheckIcon sx={{ fontSize: '0.8rem' }} />
    </IconButton>
  );
}

const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v === 0) return '0.000';
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v <= 0) return '—';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return v.toFixed(2);
};

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.2,
  px: 0.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

// Display-side mirrors of `RateCalculator.netTakerAsk` / `calculatePositionTna`
// for the taker-taker BUY scenario on the opener. Kalshi taker fee 7%,
// Polymarket category-dependent. Kept in sync with
// `arbitrage-core/services/position-opener/src/constants.ts`.
const KALSHI_TAKER_FEE_RATE = 0.07;
const polymarketTakerFeeRate = (category?: string): number => {
  if (category === 'sports') return 0.03;
  if (category === 'crypto') return 0.072;
  return 0.072;
};
const allInKalshiAsk = (raw: number): number =>
  raw + KALSHI_TAKER_FEE_RATE * raw * (1 - raw);
const allInPolymarketAsk = (raw: number, category?: string): number => {
  const rate = polymarketTakerFeeRate(category);
  return raw + rate * raw * (1 - raw);
};

const MIN_DAYS = 1 / 24;
const fmtPct = (v: number | null): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};
// Continuous-compounding TNA for a path where BOTH legs are taken as taker.
// `sum` is the all-in BUY cost (counter ask + taker fee on each leg). Returns
// null when the sum is non-positive or the event has fully expired (matches
// the backend's `MIN_DAYS = 1/24` floor + −1 cutoff).
const takerTnaFromSum = (
  sum: number | null,
  daysToExpiry: number | null | undefined
): number | null => {
  if (sum == null || sum <= 0 || daysToExpiry == null) return null;
  if (daysToExpiry < -1) return null;
  const days = Math.max(MIN_DAYS, daysToExpiry);
  const profit = 1 / sum - 1;
  return (Math.log(1 + profit) / (days / 365)) * 100;
};

export interface CreateEntrySumRuleParams {
  pair: ManualConfirmedPair;
  /**
   * Which of the 4 per-(platform, side) maker flags this row activated
   * on. Drives the rule's initial path (A or B) and which leg the
   * engine will rest as maker on the first cycle. EntryPage maps this
   * to the right kalshiConfig.side / polymarketConfig.side combo when
   * POSTing the new rule.
   *
   * Null when the row activates via the Taker-Taker switch with no
   * maker flag set — the backend defaults to Path A (polyYesMaker)
   * solely to derive sides, but the engine ignores maker flags when
   * `entryStrategy === 'taker-taker'`.
   */
  makerFlag: MakerFlag | null;
  /**
   * Optional initial strategy override. Defaults to 'maker-first' on
   * the backend. Set to 'taker-taker' when the row activates via the
   * TT switch before any maker flag is on.
   */
  entryStrategy?: 'maker-first' | 'taker-taker';
  thresholdSum: number;
  size: number;
  maxVolumeUsd: number;
}

interface Props {
  pairs: ManualConfirmedPair[];
  /** Rules of the `sum_of_sides` type — drives row controls. */
  rules: EntryRule[] | undefined;
  /** All rules (any type) keyed by pairId — used for status indicator. */
  anyRulesByPairId: Map<string, EntryRule>;
  getState: (id: string) => EnrichedEntryStatus | undefined;
  /**
   * Returns a Promise so the row can await the POST in flows that
   * commit local state on success (the per-input ✓ on pre-rule rows
   * needs to know when the create lands to clear its dirty marks).
   * Other call sites can still fire-and-forget — Promise<void> is
   * compatible with the existing un-awaited callers.
   */
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  /**
   * Toggle one of the 4 per-(platform, side) maker flags on an
   * existing rule. `clearOthers` lets the caller atomically clear
   * cross-path flags when switching paths so the backend validator
   * doesn't reject a transient cross-path state.
   */
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[],
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  /**
   * Hide a pair from the opener page (and drop its WS subscription
   * via the pair-manager change stream). Pair stays manual-confirmed
   * everywhere else — matchedBy is NOT changed.
   */
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

type SortKey = 'name' | 'sum' | 'delta' | 'tna';
type SortDir = 'asc' | 'desc';

export const EntryTabSumOfSides = ({
  pairs,
  rules,
  anyRulesByPairId,
  getState,
  onCreateRule,
  onSetMakerFlag,
  onUpdateRule,
  onHidePair
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('tna');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      map.set(rule.pairId, rule);
    }
    return map;
  }, [rules]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        // 'delta' wants the best entry first (smallest sum → largest
        // positive delta → asc). 'tna', 'sum', 'name' default to desc
        // (highest TNA / highest sum / Z→A).
        setSortDir(key === 'delta' ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  // Compute live sum + delta per pair from current sockets/state. The
  // displayed sum is the LOWER of (sumPathA, sumPathB) per product
  // spec; the row's market-data cells track the same path. Sort
  // direction defaults to "ascending delta first" so the best entry
  // candidates bubble to the top.
  const sortedPairs = useMemo(() => {
    const annotated = pairs.map(pair => {
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      const liveState =
        (pair._id ? getState(pair._id) : undefined) ??
        (rule ? getState(rule._id) : undefined);
      // Same fallback as the row body: derive sums from market
      // quotes when the rule-side value is missing so rule-less
      // pairs still sort by their live sum.
      const polyYesAskP = liveState?.polyYesAsk?.price ?? null;
      const polyNoAskP = liveState?.polyNoAsk?.price ?? null;
      const kalshiYesAskP = liveState?.kalshiYesAsk?.price ?? null;
      const kalshiNoAskP = liveState?.kalshiNoAsk?.price ?? null;
      const sumA =
        liveState?.sumPathA ??
        (polyYesAskP != null && kalshiNoAskP != null
          ? polyYesAskP + kalshiNoAskP
          : null);
      const sumB =
        liveState?.sumPathB ??
        (polyNoAskP != null && kalshiYesAskP != null
          ? polyNoAskP + kalshiYesAskP
          : null);
      // When the rule is committed (contracts already bought) the row
      // is locked to its committed path — sorting must use that sum
      // instead of the lower one or the operator can't track their
      // own committed entry.
      const committed = liveState?.committedPath ?? null;
      const grossSum =
        committed === 'a'
          ? sumA
          : committed === 'b'
            ? sumB
            : sumA != null && sumB != null
              ? Math.min(sumA, sumB)
              : (sumA ?? sumB);
      const threshold = rule?.thresholdSum ?? DEFAULT_THRESHOLD;
      const delta = grossSum != null ? threshold - grossSum : null;

      const hasSim = grossSum != null;

      // Mirror the row body: TNA is computed from the all-in asks
      // (ask + per-leg taker fee) of the DISPLAYED path
      // (committed → lower-sum → rule-flag → 'a'), against
      // liveState.daysToExpiry. Sort metric must match the cell
      // value or the column flickers when re-sorted.
      const rulePathFromFlags: 'a' | 'b' | null = rule
        ? rule.polyYesMaker || rule.kalshiNoMaker
          ? 'a'
          : rule.polyNoMaker || rule.kalshiYesMaker
            ? 'b'
            : null
        : null;
      const annotatedLowerPath: 'a' | 'b' | null =
        liveState?.lowerPath ??
        (sumA != null && sumB != null
          ? sumA <= sumB
            ? 'a'
            : 'b'
          : sumA != null
            ? 'a'
            : sumB != null
              ? 'b'
              : null);
      const displayPath: 'a' | 'b' =
        committed ?? annotatedLowerPath ?? rulePathFromFlags ?? 'a';
      const pAskForTna =
        displayPath === 'a'
          ? polyYesAskP
          : polyNoAskP;
      const kAskForTna =
        displayPath === 'a'
          ? kalshiNoAskP
          : kalshiYesAskP;
      const takerSum =
        pAskForTna != null && kAskForTna != null
          ? allInPolymarketAsk(pAskForTna, rule?.category) +
            allInKalshiAsk(kAskForTna)
          : null;
      const tna = takerTnaFromSum(takerSum, liveState?.daysToExpiry ?? null);

      const metric =
        sortKey === 'sum'
          ? grossSum
          : sortKey === 'delta'
            ? delta
            : sortKey === 'tna'
              ? tna
              : null;
      return { pair, metric, hasSim, grossSum };
    });
    annotated.sort((a, b) => {
      if (sortKey === 'name') {
        const an = a.pair.pairName ?? '';
        const bn = b.pair.pairName ?? '';
        return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      }
      // Pairs with no live sim sink to the bottom regardless of direction.
      if (a.hasSim !== b.hasSim) return a.hasSim ? -1 : 1;
      if (a.metric == null && b.metric == null) return 0;
      if (a.metric == null) return 1;
      if (b.metric == null) return -1;
      return sortDir === 'desc' ? b.metric - a.metric : a.metric - b.metric;
    });
    return annotated.map(x => x.pair);
  }, [pairs, rulesByPairId, getState, sortKey, sortDir]);

  if (pairs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No manually confirmed pairs found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' }
  } as const;

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ ...gSx, width: 260, top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
            >
              <TableSortLabel
                active={sortKey === 'name'}
                direction={sortKey === 'name' ? sortDir : 'asc'}
                onClick={() => handleSortClick('name')}
                sx={sortLabelSx}
              >
                Market
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={8}>
              Market Data
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={3}>
              Parameters
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={4}>
              Live Sums (ASK)
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={4}>
              Sim
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={5}>
              Ctrl
            </TableCell>
          </TableRow>
          <TableRow>
            {/* Market data — 4 cells per platform: Qty · Bid · Ask · Qty.
                The cells render the side data for the row's displayed
                path (committed if locked, else lower-sum path). */}
            <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Bid</TableCell>
            <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
            <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Bid</TableCell>
            <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
            <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 40, top: 22, backgroundColor: '#1a1a2e' }} align="right">Thr</TableCell>
            <TableCell sx={{ ...hSx, width: 30, top: 22, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
            <TableCell sx={{ ...hSx, width: 36, top: 22, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
            {/* Live Sums — Sum-A and Sum-B always render; lower is
                highlighted. Δ is computed against the rule's active
                trading path (committed/flag-derived). */}
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e' }}
              align="right"
              title="Path A sum: poly_yes_ask + kalshi_no_ask"
            >
              Sum-A
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e' }}
              align="right"
              title="Path B sum: poly_no_ask + kalshi_yes_ask"
            >
              Sum-B
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Threshold − active path sum (positive = below threshold = good entry). Click to sort."
              onClick={() => handleSortClick('delta')}
            >
              <TableSortLabel
                active={sortKey === 'delta'}
                direction={sortKey === 'delta' ? sortDir : 'asc'}
                sx={sortLabelSx}
              >
                Δ
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Annualized return if BOTH legs are bought as taker on the displayed path right now (all-in cost including taker fees, continuous compounding). Click to sort."
              onClick={() => handleSortClick('tna')}
            >
              <TableSortLabel
                active={sortKey === 'tna'}
                direction={sortKey === 'tna' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                TNA
              </TableSortLabel>
            </TableCell>
            {/* Sim — per-leg maker scenarios. Orange = counter-leg net
                taker (what we'd pay on the other platform), Blue = our
                maker rest price (= threshold − orange). 4 cells, one
                per (platform, side) maker. */}
            <TableCell sx={{ ...hSx, width: 38, top: 22, backgroundColor: '#1a1a2e' }} align="right" title="Poly YES as maker — counter (Kalshi NO) taker / our maker rest">
              PY
            </TableCell>
            <TableCell sx={{ ...hSx, width: 38, top: 22, backgroundColor: '#1a1a2e' }} align="right" title="Poly NO as maker — counter (Kalshi YES) taker / our maker rest">
              PN
            </TableCell>
            <TableCell sx={{ ...hSx, width: 38, top: 22, backgroundColor: '#1a1a2e' }} align="right" title="Kalshi YES as maker — counter (Poly NO) taker / our maker rest">
              KY
            </TableCell>
            <TableCell sx={{ ...hSx, width: 38, top: 22, backgroundColor: '#1a1a2e' }} align="right" title="Kalshi NO as maker — counter (Poly YES) taker / our maker rest">
              KN
            </TableCell>
            {/* Ctrl — 4 maker toggles. Path-exclusive: turning on a
                path-B toggle while a path-A toggle is on prompts a
                confirmation dialog. Locked to one path once contracts
                have been bought. */}
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center" title="Poly YES maker (Path A)">PY</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center" title="Poly NO maker (Path B)">PN</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center" title="Kalshi YES maker (Path B)">KY</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center" title="Kalshi NO maker (Path A)">KN</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center" title="Taker-Taker strategy: fire BOTH legs as parallel takers when combined gross cost (both taker fees) clears threshold. Independent of the 4 maker flags.">TT</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPairs.map(pair => {
            const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
            const anyRule = pair._id ? anyRulesByPairId.get(pair._id) : undefined;
            const liveState =
              (pair._id ? getState(pair._id) : undefined) ??
              (rule ? getState(rule._id) : undefined);
            return (
              <SumRow
                key={pair._id ?? pair.pairName}
                pair={pair}
                rule={rule}
                anyRule={anyRule}
                liveState={liveState}
                onCreateRule={onCreateRule}
                onSetMakerFlag={onSetMakerFlag}
                onUpdateRule={onUpdateRule}
                onHidePair={onHidePair}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface RowProps {
  pair: ManualConfirmedPair;
  rule?: EntryRule;
  anyRule?: EntryRule;
  liveState?: EnrichedEntryStatus;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[],
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

// Path → which flags belong to it. Used for the cross-path switch
// confirmation: turning on a flag from a different path needs to clear
// the current path's flags atomically so the backend's cross-path
// validator doesn't reject the transient state.
const PATH_FLAGS: Record<'a' | 'b', MakerFlag[]> = {
  a: ['polyYesMaker', 'kalshiNoMaker'],
  b: ['polyNoMaker', 'kalshiYesMaker'],
};
const FLAG_PATH: Record<MakerFlag, 'a' | 'b'> = {
  polyYesMaker: 'a',
  kalshiNoMaker: 'a',
  polyNoMaker: 'b',
  kalshiYesMaker: 'b',
};
const FLAG_LABEL: Record<MakerFlag, string> = {
  polyYesMaker: 'Poly YES',
  polyNoMaker: 'Poly NO',
  kalshiYesMaker: 'Kalshi YES',
  kalshiNoMaker: 'Kalshi NO',
};

const SumRow = memo(({
  pair,
  rule,
  anyRule,
  liveState,
  onCreateRule,
  onSetMakerFlag,
  onUpdateRule,
  onHidePair
}: RowProps) => {
  const ruleSize =
    rule?.kalshiConfig?.contractsToBuy ??
    rule?.polymarketConfig?.contractsToBuy ??
    DEFAULT_SIZE;
  const initThreshold =
    rule?.thresholdSum != null ? String(rule.thresholdSum) : String(DEFAULT_THRESHOLD);
  const initSize = String(ruleSize);
  const initMaxVol = String(rule?.maxVolumeUsd ?? DEFAULT_MAX_VOL);

  const [threshold, setThreshold] = useState(initThreshold);
  const [size, setSize] = useState(initSize);
  const [maxVol, setMaxVol] = useState(initMaxVol);
  const [savedThreshold, setSavedThreshold] = useState(initThreshold);
  const [savedSize, setSavedSize] = useState(initSize);
  const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Per-flag spinner state (4 toggles, not 2). A single flag in
  // flight at a time is the common case; the map is structurally
  // open so a quick double-toggle doesn't drop the second spinner.
  const [togglingFlag, setTogglingFlag] = useState<MakerFlag | null>(null);
  // Confirm-then-hide flow for the "remove pair from opener page"
  // action. Opens on trash-icon click; on Confirm the PATCH lands +
  // the optimistic cache patch removes the row immediately.
  const [hideDialogOpen, setHideDialogOpen] = useState(false);
  const [hidePending, setHidePending] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    flag: MakerFlag;
    /**
     * Path being activated (derived from `flag`). When the rule
     * already has the opposite path's flags on, this dialog also
     * carries a path-switch warning and clears the old flags
     * atomically on confirm.
     */
    targetPath: 'a' | 'b';
    /**
     * Old-path flags that must be cleared to avoid a transient
     * cross-path BadRequest. Empty when the rule isn't switching
     * paths (just activating a first flag or in-path inclusive
     * add).
     */
    clearOthers: MakerFlag[];
    /** Snapshot of the target path's asks for drift detection. */
    snappedSum: number | null;
  } | null>(null);

  // Keep inputs in sync with rule changes only when user is not mid-edit.
  useEffect(() => {
    if (!rule) return;
    const dirty =
      threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;
    if (dirty) return;
    if (rule.thresholdSum != null) {
      const next = String(rule.thresholdSum);
      if (next !== savedThreshold) {
        setThreshold(next);
        setSavedThreshold(next);
      }
    }
    const ruleSizeNow =
      rule.kalshiConfig?.contractsToBuy ?? rule.polymarketConfig?.contractsToBuy;
    if (ruleSizeNow != null) {
      const next = String(ruleSizeNow);
      if (next !== savedSize) {
        setSize(next);
        setSavedSize(next);
      }
    }
    const nextMax = String(rule.maxVolumeUsd);
    if (nextMax !== savedMaxVol) {
      setMaxVol(nextMax);
      setSavedMaxVol(nextMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rule?.thresholdSum,
    rule?.maxVolumeUsd,
    rule?.kalshiConfig?.contractsToBuy,
    rule?.polymarketConfig?.contractsToBuy
  ]);

  const isDirty =
    threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;

  const validateThreshold = useCallback(
    (value: string): string | null => {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n <= 0) return 'Must be > 0';
      if (n >= 1) return 'Must be < 1.00 (hard cap)';
      return null;
    },
    []
  );

  // Live error message updates as the user types.
  useEffect(() => {
    setThresholdError(validateThreshold(threshold));
  }, [threshold, validateThreshold]);

  const handleSave = useCallback(async () => {
    if (!isDirty || !rule) return;
    const tErr = validateThreshold(threshold);
    if (tErr) {
      setThresholdError(tErr);
      return;
    }
    const parsedThreshold = Number.parseFloat(threshold);
    const parsedSize = Number.parseInt(size, 10);
    const parsedMaxVol = Number.parseFloat(maxVol);
    const dto: UpdateEntryRuleDto = {};
    if (
      Number.isFinite(parsedThreshold) &&
      parsedThreshold > 0 &&
      parsedThreshold < 1 &&
      threshold !== savedThreshold
    ) {
      dto.thresholdSum = parsedThreshold;
    }
    if (Number.isFinite(parsedMaxVol) && parsedMaxVol > 0 && maxVol !== savedMaxVol) {
      dto.maxVolumeUsd = parsedMaxVol;
    }
    if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
      if (rule.kalshiConfig) {
        dto.kalshiConfig = {
          side: rule.kalshiConfig.side,
          ticker: rule.kalshiConfig.ticker,
          contractsToBuy: parsedSize
        };
      }
      if (rule.polymarketConfig) {
        dto.polymarketConfig = {
          side: rule.polymarketConfig.side,
          yesTokenId: rule.polymarketConfig.yesTokenId,
          noTokenId: rule.polymarketConfig.noTokenId,
          conditionId: rule.polymarketConfig.conditionId,
          contractsToBuy: parsedSize
        };
      }
    }
    setSaving(true);
    const ok = await onUpdateRule(rule, dto);
    if (ok) {
      setSavedThreshold(threshold);
      setSavedSize(size);
      setSavedMaxVol(maxVol);
    }
    setSaving(false);
  }, [
    isDirty,
    threshold,
    size,
    maxVol,
    savedThreshold,
    savedSize,
    savedMaxVol,
    rule,
    onUpdateRule,
    validateThreshold
  ]);

  // Pre-rule commit: when the row has no rule yet and the operator
  // typed values that differ from the defaults, the per-input ✓
  // creates a PAUSED rule carrying those values. No maker flag is
  // set — the row lands paused (schema default) and the operator
  // later activates by toggling one of the 4 maker switches (the
  // `entry.rule.updated` auto-resume in RulesService promotes the
  // paused row to active when a flag flips false→true). Without
  // this, the typed values can only land via the maker-flag toggle
  // itself, which couples "I want these params saved" to "and start
  // trading right now" — two distinct operator intents.
  const handleCreatePausedRule = useCallback(async () => {
    if (!isDirty || rule || !pair._id) return;
    const tErr = validateThreshold(threshold);
    if (tErr) {
      setThresholdError(tErr);
      return;
    }
    const parsedThr = Number.parseFloat(threshold);
    const parsedSize = Number.parseInt(size, 10);
    const parsedMaxV = Number.parseFloat(maxVol);
    if (!Number.isFinite(parsedThr) || parsedThr <= 0 || parsedThr >= 1) return;
    if (!Number.isFinite(parsedSize) || parsedSize <= 0) return;
    if (!Number.isFinite(parsedMaxV) || parsedMaxV <= 0) return;
    setSaving(true);
    try {
      await onCreateRule({
        pair,
        makerFlag: null,
        thresholdSum: parsedThr,
        size: parsedSize,
        maxVolumeUsd: parsedMaxV,
      });
      setSavedThreshold(threshold);
      setSavedSize(size);
      setSavedMaxVol(maxVol);
    } finally {
      setSaving(false);
    }
  }, [
    isDirty,
    rule,
    pair,
    threshold,
    size,
    maxVol,
    validateThreshold,
    onCreateRule,
  ]);

  // Single dispatcher used by every ✓ click and Enter keypress. PATCH
  // when a rule exists, POST a paused rule when not. onBlur stays on
  // the PATCH-only path (handleSave) so tabbing through inputs never
  // accidentally CREATES a rule — a much bigger action than saving an
  // edit.
  const handleCommit = useCallback(async () => {
    if (rule) {
      await handleSave();
    } else {
      await handleCreatePausedRule();
    }
  }, [rule, handleSave, handleCreatePausedRule]);

  // ── Two-path market data + path resolution ─────────────────────────────
  // The enricher emits both sides' quotes; the row picks which to
  // display based on the active path. Sum-A and Sum-B always render.
  //
  // Sums are market-derived (no rule required), so compute them from
  // the 8 quotes on liveState and only fall back to the rule-side
  // value when one of the quotes is missing. Pairs that don't yet
  // have a rule still get sums rendered as long as the market data
  // is flowing — which is the whole point of the Sum-A / Sum-B
  // columns being "always render".
  const polyYesAskPrice = liveState?.polyYesAsk?.price ?? null;
  const polyNoAskPrice = liveState?.polyNoAsk?.price ?? null;
  const kalshiYesAskPrice = liveState?.kalshiYesAsk?.price ?? null;
  const kalshiNoAskPrice = liveState?.kalshiNoAsk?.price ?? null;
  const computedSumPathA =
    polyYesAskPrice != null && kalshiNoAskPrice != null
      ? polyYesAskPrice + kalshiNoAskPrice
      : null;
  const computedSumPathB =
    polyNoAskPrice != null && kalshiYesAskPrice != null
      ? polyNoAskPrice + kalshiYesAskPrice
      : null;
  const sumPathA = liveState?.sumPathA ?? computedSumPathA;
  const sumPathB = liveState?.sumPathB ?? computedSumPathB;
  // lowerPath is also derivable from sums — only fall back to the
  // rule-side value when sums are missing.
  const lowerPath: 'a' | 'b' | null =
    liveState?.lowerPath ??
    (sumPathA != null && sumPathB != null
      ? sumPathA <= sumPathB
        ? 'a'
        : 'b'
      : sumPathA != null
        ? 'a'
        : sumPathB != null
          ? 'b'
          : null);
  const committedPath = liveState?.committedPath ?? null;
  // Path implied by the rule's flag state. With no rule yet, the row
  // is a "pre-rule" candidate and defaults to path A's display until
  // the operator picks a toggle.
  const rulePathFromFlags: 'a' | 'b' | null = rule
    ? rule.polyYesMaker || rule.kalshiNoMaker
      ? 'a'
      : rule.polyNoMaker || rule.kalshiYesMaker
        ? 'b'
        : null
    : null;
  // Display path: committed (locked) → lower (cheaper hint) → rule
  // path → A as final fallback. Drives market-data cells + sum
  // highlight.
  const displayPath: 'a' | 'b' =
    committedPath ?? lowerPath ?? rulePathFromFlags ?? 'a';
  // Trading path: committed → rule's flag-derived path. What the
  // engine will actually buy — and therefore what the hard-cap gate
  // must compare against.
  const tradingPath: 'a' | 'b' | null =
    committedPath ?? rulePathFromFlags;

  // YES/NO chip next to each platform title reflects the displayed
  // book — driven by `displayPath` (committed → lower-sum → rule-flag
  // → 'a'), NOT the pair's static stored side. Path A = poly YES +
  // kalshi NO; Path B = poly NO + kalshi YES. Operator can see at a
  // glance which side of each book the row's quotes + sums are
  // pulling from RIGHT NOW.
  const displayPolySide: 'yes' | 'no' = displayPath === 'a' ? 'yes' : 'no';
  const displayKalshiSide: 'yes' | 'no' = displayPath === 'a' ? 'no' : 'yes';

  const pickPolyAsk = (p: 'a' | 'b') =>
    p === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk;
  const pickPolyBid = (p: 'a' | 'b') =>
    p === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid;
  const pickKalshiAsk = (p: 'a' | 'b') =>
    p === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk;
  const pickKalshiBid = (p: 'a' | 'b') =>
    p === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid;

  // Display-path quotes (rendered in the row's market-data cells).
  const pAsk = pickPolyAsk(displayPath)?.price ?? null;
  const pBid = pickPolyBid(displayPath)?.price ?? null;
  const kAsk = pickKalshiAsk(displayPath)?.price ?? null;
  const kBid = pickKalshiBid(displayPath)?.price ?? null;
  const pAskSize = pickPolyAsk(displayPath)?.size;
  const pBidSize = pickPolyBid(displayPath)?.size;
  const kAskSize = pickKalshiAsk(displayPath)?.size;
  const kBidSize = pickKalshiBid(displayPath)?.size;

  const thresholdNum = Number.parseFloat(threshold);
  const thresholdValid = Number.isFinite(thresholdNum) && thresholdNum > 0 && thresholdNum < 1;
  // grossSum (Δ + hard-cap gate) tracks the trading path — what the
  // engine will actually buy. When the rule has no flags set yet, fall
  // back to the lower-sum path so the operator can see whether
  // activating either path would meet the threshold.
  const grossSum =
    tradingPath === 'a'
      ? sumPathA
      : tradingPath === 'b'
        ? sumPathB
        : (lowerPath === 'a' ? sumPathA : lowerPath === 'b' ? sumPathB : null);
  const delta = grossSum != null && thresholdValid ? thresholdNum - grossSum : null;
  const meetsThreshold = delta != null && delta >= 0;
  const overHardCap = grossSum != null && grossSum >= ENTRY_HARD_CAP_GROSS;

  // Taker TNA: annualized return if BOTH legs are bought as taker on the
  // displayed path right now. All-in cost = ask + taker fee per leg (Kalshi
  // 7%, Polymarket category-dependent), then continuous-compounding TNA on
  // sum vs daysToExpiry. Mirrors the backend RateCalculator.calculatePositionTna
  // formula for the BUY direction (sum = makerPrice + allInCounterAsk, with
  // both terms here being all-in counter asks since both legs are taker).
  const category = rule?.category;
  const pAskForTna = pickPolyAsk(displayPath)?.price ?? null;
  const kAskForTna = pickKalshiAsk(displayPath)?.price ?? null;
  const takerSum =
    pAskForTna != null && kAskForTna != null
      ? allInPolymarketAsk(pAskForTna, category) + allInKalshiAsk(kAskForTna)
      : null;
  const takerTna = takerTnaFromSum(
    takerSum,
    liveState?.daysToExpiry ?? null
  );

  const inSx = {
    '& .MuiInputBase-input': {
      fontSize: '0.62rem',
      py: 0.15,
      px: 0.2,
      textAlign: 'right',
      width: 28
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.08)'
    },
    minWidth: 0
  } as const;

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontSize: '0.7rem' }}>
        {pair.polymarketTitle || pair.pairName}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
        {pair.kalshiTitle || pair.pairName}
      </Typography>
    </Box>
  );

  // Hard-cap UX layers (the BACKEND cap is the real safety contract —
  // see evaluation.service.ts: assertWithinHardCap is re-read with the
  // live counter ask on every placement attempt):
  //   1. threshold input rejects >= 1.0 (validator) — this is the only
  //      cap that gates ACTIVATION, because as maker we rest at the
  //      limit price and the live taker sum is irrelevant.
  //   2. row outline goes red when the live taker grossSum >= 0.995 —
  //      purely informational ("a taker round-trip right now would be
  //      a guaranteed loss"); does NOT block maker activation.
  const rowSx = overHardCap
    ? {
        '&:hover': { backgroundColor: 'rgba(244,67,54,0.05)' },
        outline: '1px solid rgba(244,67,54,0.5)'
      }
    : { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } };

  // Which of the 4 maker flags are currently on (resolves to false when
  // there's no rule yet — pre-rule rows show all four toggles as off).
  const flagState: Record<MakerFlag, boolean> = {
    polyYesMaker: rule?.polyYesMaker === true,
    polyNoMaker: rule?.polyNoMaker === true,
    kalshiYesMaker: rule?.kalshiYesMaker === true,
    kalshiNoMaker: rule?.kalshiNoMaker === true,
  };
  const anyPathAOn =
    flagState.polyYesMaker || flagState.kalshiNoMaker;
  const anyPathBOn =
    flagState.polyNoMaker || flagState.kalshiYesMaker;

  // Apply a flag change — creates the rule on first activation,
  // otherwise PATCHes the existing rule. `clearOthers` is forwarded
  // so a path-switch can clear the old-path flags atomically.
  const doSetFlag = useCallback(
    async (flag: MakerFlag, value: boolean, clearOthers: MakerFlag[] = []) => {
      setTogglingFlag(flag);
      try {
        if (rule) {
          await onSetMakerFlag(rule, flag, value, clearOthers);
        } else if (value) {
          const tErr = validateThreshold(threshold);
          if (tErr) {
            setThresholdError(tErr);
            return;
          }
          const parsedThr = Number.parseFloat(threshold);
          const parsedSize = Number.parseInt(size, 10);
          const parsedMaxV = Number.parseFloat(maxVol);
          onCreateRule({
            pair,
            makerFlag: flag,
            thresholdSum:
              Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                ? parsedThr
                : DEFAULT_THRESHOLD,
            size:
              Number.isFinite(parsedSize) && parsedSize > 0
                ? parsedSize
                : DEFAULT_SIZE,
            maxVolumeUsd:
              Number.isFinite(parsedMaxV) && parsedMaxV > 0
                ? parsedMaxV
                : DEFAULT_MAX_VOL,
          });
        }
      } finally {
        // Spinner unsticks once the mutation lands; the 2 s fallback
        // is defensive for cases where the optimistic patch races
        // ahead of the server response.
        setTimeout(() => setTogglingFlag(null), 2000);
      }
    },
    [
      rule,
      threshold,
      size,
      maxVol,
      pair,
      onCreateRule,
      onSetMakerFlag,
      validateThreshold,
    ],
  );

  // Click handler for the 4 toggle switches. Opens the activation
  // dialog on turn-on (with path-switch warning when crossing paths)
  // and applies turn-off directly.
  const handleToggleFlag = useCallback(
    (flag: MakerFlag, value: boolean) => {
      if (!value) {
        // Turning a flag off can't violate any backend invariant —
        // no dialog needed.
        doSetFlag(flag, false);
        return;
      }
      const targetPath = FLAG_PATH[flag];
      // Detect a path switch: rule has flags on the OTHER path. The
      // dialog warns the operator and the confirm action passes
      // clearOthers so the PATCH lands as an atomic flip.
      const oldPath: 'a' | 'b' | null =
        targetPath === 'a' && anyPathBOn
          ? 'b'
          : targetPath === 'b' && anyPathAOn
            ? 'a'
            : null;
      const clearOthers: MakerFlag[] = oldPath
        ? PATH_FLAGS[oldPath].filter((f) => flagState[f])
        : [];
      const targetSum = targetPath === 'a' ? sumPathA : sumPathB;
      setConfirmDialog({
        flag,
        targetPath,
        clearOthers,
        snappedSum: targetSum,
      });
    },
    [anyPathAOn, anyPathBOn, flagState, sumPathA, sumPathB, doSetFlag],
  );

  // Live path sum on the target path — informational. Closure
  // captures the latest WS values; never the snapped one.
  const confirmTargetSum = confirmDialog
    ? confirmDialog.targetPath === 'a'
      ? sumPathA
      : sumPathB
    : null;
  // The dialog only activates maker rules, so the contract is the
  // threshold — not the live taker sum. As maker we rest at our
  // limit price; the backend hard-cap fortress (evaluation.service)
  // re-reads the live counter ask on every cycle and refuses to
  // place when maker_rest + counter_ask >= cap. Gate Activate on
  // the threshold instead.
  const confirmOverHardCap =
    Number.isFinite(thresholdNum) && thresholdNum >= ENTRY_HARD_CAP_GROSS;

  const handleConfirmToggle = useCallback(() => {
    if (!confirmDialog) return;
    if (confirmOverHardCap) return;
    doSetFlag(confirmDialog.flag, true, confirmDialog.clearOthers);
    setConfirmDialog(null);
  }, [confirmDialog, confirmOverHardCap, doSetFlag]);

  // "Re-confirm": if the target path's sum drifted > 1 tick since the
  // dialog opened, force the operator to acknowledge. Bid drift is
  // irrelevant to the activation calculus on the BUY side — we
  // compare the path sum directly.
  const TICK = 0.01;
  const priceDriftedSinceOpen = useMemo(() => {
    if (!confirmDialog) return false;
    if (confirmDialog.snappedSum == null || confirmTargetSum == null) return false;
    return Math.abs(confirmTargetSum - confirmDialog.snappedSum) > TICK;
  }, [confirmDialog, confirmTargetSum]);

  const handleReconfirm = useCallback(() => {
    if (!confirmDialog) return;
    setConfirmDialog({
      ...confirmDialog,
      snappedSum: confirmTargetSum,
    });
  }, [confirmDialog, confirmTargetSum]);

  const ruleStatusColor =
    anyRule?.status === 'active'
      ? '#4caf50'
      : anyRule?.status === 'paused'
      ? '#ff9800'
      : 'grey.600';

  return (
    <>
    <TableRow sx={rowSx}>
      {/* Market */}
      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
          {/* Rule status indicator: green=active, orange=paused,
              dim=none. Mirrors the unwinder's debug icon position so
              the layout is consistent. */}
          <Tooltip
            title={
              anyRule
                ? `Entry rule: ${anyRule.status}`
                : 'No rule yet — toggle a platform to create'
            }
            placement="top"
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: ruleStatusColor,
                mt: 0.5,
                ml: 0.2,
                flexShrink: 0
              }}
            />
          </Tooltip>
          {/* Engine state chip intentionally NOT rendered here. The
              Opening page is operator-oriented (configure + activate
              + watch the sums); engine state-machine progression is
              surfaced on the Unwinding page and the Notifications
              feed where it belongs. Keeping it out of this row also
              means pair-only rows (no active rule, state = 'no_rule')
              don't get a useless "NO_RULE" chip. */}
          {/* Hide-pair (trash) button — removes the pair from the
              opener page + drops its WS subscription on the backend.
              Does NOT change the pair's matchedBy state, so it stays
              visible elsewhere (Opportunities, Positions, Unwinding). */}
          <Tooltip title="Remove pair from opener page" placement="top">
            <span>
              <IconButton
                size="small"
                disabled={hidePending}
                onClick={(e) => {
                  e.stopPropagation();
                  setHideDialogOpen(true);
                }}
                sx={{
                  p: 0,
                  ml: 0.4,
                  color: hidePending ? 'grey.700' : 'grey.500',
                  '&:hover': { color: '#f44336' },
                  flexShrink: 0,
                }}
              >
                {hidePending ? (
                  <CircularProgress size={12} />
                ) : (
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={tooltipTitle} placement="top-start">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <a
                  href={pair.polymarket?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined}
                  target="_blank"
                  rel="noopener"
                  onClick={e => e.stopPropagation()}
                >
                  <img
                    src="/assets/exchanges/png/100x100/polymarket.png"
                    alt="P"
                    style={{ width: 13, height: 13, borderRadius: '50%' }}
                  />
                </a>
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {pair.polymarketTitle || pair.pairName}
                </Typography>
                <Chip
                  label={displayPolySide.toUpperCase()}
                  size="small"
                  color={displayPolySide === 'yes' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{
                    fontSize: '0.42rem',
                    height: 12,
                    '& .MuiChip-label': { px: 0.25 },
                    opacity: 0.7
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <a
                  href={pair.kalshi?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined}
                  target="_blank"
                  rel="noopener"
                  onClick={e => e.stopPropagation()}
                >
                  <img
                    src="/assets/exchanges/png/100x100/kalshi.png"
                    alt="K"
                    style={{ width: 13, height: 13, borderRadius: '50%' }}
                  />
                </a>
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: 'grey.400',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {pair.kalshiTitle || pair.pairName}
                </Typography>
                <Chip
                  label={displayKalshiSide.toUpperCase()}
                  size="small"
                  color={displayKalshiSide === 'yes' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{
                    fontSize: '0.42rem',
                    height: 12,
                    '& .MuiChip-label': { px: 0.25 },
                    opacity: 0.7
                  }}
                />
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Market Data — both bid and ask are now relevant. The maker
          leg rests at its BID (we BUY there), the counter leg hits the
          counter ASK (we BUY there too). 4 cells per platform; data
          reflects the row's displayed path. */}
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pBidSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>{fmtPrice(pBid)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pAsk)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pAskSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kBidSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>{fmtPrice(kBid)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kAsk)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kAskSize)}</Typography>
      </TableCell>

      {/* Parameters — each field gets its OWN save indicator next to
          the input so the operator sees feedback on the exact field
          they edited. Without per-field ticks, a single trailing tick
          (in the MaxV cell) was easy to miss when editing Threshold
          or Size. `saving` spins on whichever field's currently
          mid-PATCH (one PATCH covers all changed fields). */}
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Tooltip title={thresholdError ?? ''} placement="top" disableHoverListener={!thresholdError}>
          <span>
            <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
              <TextField
                variant="outlined"
                size="small"
                value={threshold}
                placeholder="0.95"
                onChange={e => setThreshold(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                error={!!thresholdError}
                helperText={thresholdError ?? ''}
                sx={inSx}
                inputProps={{ step: 0.01, min: 0, max: 0.999 }}
              />
              {renderSaveIndicator(saving, threshold !== savedThreshold, handleCommit)}
            </Stack>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <TextField
            variant="outlined"
            size="small"
            value={size}
            placeholder="50"
            onChange={e => setSize(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCommit();
            }}
            sx={inSx}
            inputProps={{ step: 1, min: 1 }}
          />
          {renderSaveIndicator(saving, size !== savedSize, handleCommit)}
        </Stack>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <TextField
            variant="outlined"
            size="small"
            value={maxVol}
            placeholder="500"
            onChange={e => setMaxVol(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCommit();
            }}
            sx={inSx}
            inputProps={{ step: 100, min: 0 }}
          />
          {renderSaveIndicator(saving, maxVol !== savedMaxVol, handleCommit)}
        </Stack>
      </TableCell>

      {/* Live Sums — both paths' sums always render. When the rule is
          committed to one path, only that path's sum is shown (the
          other is dim/strikethrough). The active path is bold and
          colored by threshold/cap state. */}
      {(['a', 'b'] as const).map((path) => {
        const sumValue = path === 'a' ? sumPathA : sumPathB;
        const isActive = displayPath === path;
        const isHiddenByLock =
          committedPath != null && committedPath !== path;
        // Color: if active and live, follow threshold/cap state;
        // otherwise dim.
        const color = isHiddenByLock
          ? 'grey.700'
          : isActive
            ? overHardCap
              ? '#f44336'
              : meetsThreshold
                ? '#4caf50'
                : '#42a5f5'
            : 'grey.500';
        return (
          <TableCell key={path} sx={cSx} align="right">
            <Tooltip
              title={
                isHiddenByLock
                  ? `Locked to path ${committedPath?.toUpperCase()} — contracts held`
                  : path === 'a'
                    ? 'poly_yes_ask + kalshi_no_ask'
                    : 'poly_no_ask + kalshi_yes_ask'
              }
              placement="top"
            >
              <Typography
                sx={{
                  fontSize: isActive ? '0.78rem' : '0.7rem',
                  fontWeight: isActive ? 700 : 500,
                  color,
                  textDecoration: isHiddenByLock ? 'line-through' : 'none',
                  opacity: isHiddenByLock ? 0.4 : 1,
                }}
              >
                {isHiddenByLock ? '—' : fmtPrice(sumValue)}
              </Typography>
            </Tooltip>
          </TableCell>
        );
      })}
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: delta == null ? 'grey.500' : delta >= 0 ? '#4caf50' : '#f44336'
          }}
        >
          {delta == null ? '—' : (delta >= 0 ? '+' : '') + delta.toFixed(3)}
        </Typography>
      </TableCell>

      {/* Taker TNA — annualized return if BOTH legs of the displayed path
          are bought as taker right now. Computed from the all-in path sum
          (asks + per-leg taker fees) and liveState.daysToExpiry using the
          same continuous-compounding formula the backend's
          RateCalculator.calculatePositionTna uses. */}
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color:
              takerTna == null
                ? 'grey.500'
                : takerTna >= 0
                  ? '#4caf50'
                  : '#f44336'
          }}
        >
          {fmtPct(takerTna)}
        </Typography>
      </TableCell>

      {/* Sim — 4 per-leg maker scenario cells (orange/blue stacked).
          Inactive-path cells are dimmed when the rule is committed
          since switching paths is blocked.

          Computed CLIENT-SIDE from the local `thresholdNum` + live
          counter asks so the preview tracks the user's typed (but
          not-yet-saved) threshold. The backend recomputes the same
          values on every WS tick using the saved `rule.thresholdSum`
          — once a PATCH lands the two converge, but until then the
          user sees what they're about to commit to instead of the
          stale server value. Mirrors backend
          state-enricher.service.ts#computeSimCells exactly:
            taker = allIn(counterAsk)   (independent of threshold)
            maker = threshold - taker. */}
      {(['polyYesMaker', 'polyNoMaker', 'kalshiYesMaker', 'kalshiNoMaker'] as MakerFlag[]).map((flag) => {
        // Per-flag counter ask + platform fee. The mapping matches
        // backend computeSimCells: e.g. polyYesMaker rests poly_yes
        // BUY, so the counter taker pays kalshi_no ASK (all-in).
        let counterAskRaw: number | null = null;
        let takerAllIn: number | null = null;
        if (flag === 'polyYesMaker') {
          counterAskRaw = kalshiNoAskPrice;
          takerAllIn = counterAskRaw != null ? allInKalshiAsk(counterAskRaw) : null;
        } else if (flag === 'polyNoMaker') {
          counterAskRaw = kalshiYesAskPrice;
          takerAllIn = counterAskRaw != null ? allInKalshiAsk(counterAskRaw) : null;
        } else if (flag === 'kalshiYesMaker') {
          counterAskRaw = polyNoAskPrice;
          takerAllIn = counterAskRaw != null ? allInPolymarketAsk(counterAskRaw, category) : null;
        } else {
          counterAskRaw = polyYesAskPrice;
          takerAllIn = counterAskRaw != null ? allInPolymarketAsk(counterAskRaw, category) : null;
        }
        const makerRest =
          takerAllIn != null && thresholdValid ? thresholdNum - takerAllIn : null;
        const haveCounter = counterAskRaw != null;
        const flagPath = FLAG_PATH[flag];
        const isHiddenByLock =
          committedPath != null && committedPath !== flagPath;
        return (
          <TableCell key={flag} sx={cSx} align="right">
            {!haveCounter || isHiddenByLock ? (
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.700' }}>—</Typography>
            ) : (
              <Stack spacing={0} alignItems="flex-end">
                <Typography sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
                  {takerAllIn == null ? '—' : fmtPrice(takerAllIn)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#42a5f5', fontWeight: 600 }}>
                  {makerRest == null ? '—' : fmtPrice(makerRest)}
                </Typography>
              </Stack>
            )}
          </TableCell>
        );
      })}

      {/* Ctrl — 4 per-(platform, side) maker toggles. Locked to the
          committed path when contracts have been bought. */}
      {(['polyYesMaker', 'polyNoMaker', 'kalshiYesMaker', 'kalshiNoMaker'] as MakerFlag[]).map((flag) => {
        const flagPath = FLAG_PATH[flag];
        const isLockedOut =
          committedPath != null && committedPath !== flagPath;
        return (
          <TableCell
            key={flag}
            sx={cSx}
            align="center"
            onClick={(e) => e.stopPropagation()}
          >
            {togglingFlag === flag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  isLockedOut
                    ? `Locked to path ${committedPath?.toUpperCase()} — contracts held`
                    : `${FLAG_LABEL[flag]} as maker (Path ${flagPath.toUpperCase()})`
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={flagState[flag]}
                    disabled={isLockedOut}
                    onChange={(_, v) => handleToggleFlag(flag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
        );
      })}

      {/* Ctrl — Taker-Taker strategy toggle. Independent of the 4 maker
          flags. Can be turned on with NO maker flag set — the rule is
          created with `entryStrategy: 'taker-taker'` and the engine
          fires both legs as takers when the combined gross cost clears
          threshold (no maker rests at all in steady state).
          When the rule is mid-balance after an asymmetric shot, a
          spinner replaces the switch so the user sees something is in
          flight. */}
      <TableCell sx={cSx} align="center" onClick={(e) => e.stopPropagation()}>
        {anyRule && (anyRule.balancingContractsRemaining ?? 0) > 0 ? (
          <Tooltip
            title={`Balancing: ${anyRule.balancingContractsRemaining} contracts resting on ${anyRule.balancingLeg}`}
            placement="top"
          >
            <CircularProgress size={14} />
          </Tooltip>
        ) : (
          <Tooltip
            title={
              (anyRule?.entryStrategy ?? 'maker-first') === 'taker-taker'
                ? 'Taker-Taker: fires BOTH legs as parallel takers when combined cost clears threshold. Click to switch off.'
                : 'Taker-Taker: turn on to fire BOTH legs as parallel takers whenever combined cost clears threshold. Independent of the 4 maker flags.'
            }
            placement="top"
          >
            <span>
              <Switch
                size="small"
                checked={(anyRule?.entryStrategy ?? 'maker-first') === 'taker-taker'}
                onChange={(_, v) => {
                  if (anyRule) {
                    void onUpdateRule(anyRule, {
                      entryStrategy: v ? 'taker-taker' : 'maker-first',
                    });
                    return;
                  }
                  // No rule yet — only meaningful direction is OFF→ON.
                  // Create a fresh rule with TT enabled and no maker
                  // flag set; the operator can still flip maker flags
                  // independently afterward.
                  if (!v) return;
                  const tErr = validateThreshold(threshold);
                  if (tErr) {
                    setThresholdError(tErr);
                    return;
                  }
                  const parsedThr = Number.parseFloat(threshold);
                  const parsedSize = Number.parseInt(size, 10);
                  const parsedMaxV = Number.parseFloat(maxVol);
                  onCreateRule({
                    pair,
                    makerFlag: null,
                    entryStrategy: 'taker-taker',
                    thresholdSum:
                      Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                        ? parsedThr
                        : DEFAULT_THRESHOLD,
                    size:
                      Number.isFinite(parsedSize) && parsedSize > 0
                        ? parsedSize
                        : DEFAULT_SIZE,
                    maxVolumeUsd:
                      Number.isFinite(parsedMaxV) && parsedMaxV > 0
                        ? parsedMaxV
                        : DEFAULT_MAX_VOL,
                  });
                }}
              />
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>

    {/* Hide-pair confirmation. Pair stays manual-confirmed
        everywhere else (Opportunities, Positions, Unwinding); only
        the opener filters it out and unsubscribes its WS market
        data via the PairsSyncService change-stream tick. */}
    <Dialog
      open={hideDialogOpen}
      onClose={() => !hidePending && setHideDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '0.9rem', pb: 0.5 }}>
        Remove pair from opener
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Pair:</b> {pair.pairName}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'grey.300', mt: 0.5 }}>
            The pair will disappear from this Opening table and the opener
            will stop subscribing to its Kalshi + Polymarket orderbooks.
            It stays manual-confirmed everywhere else (Opportunities,
            Positions, Unwinding). You can re-expose it later via the
            pair-manager API.
          </Typography>
          {anyRule && (
            <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', mt: 0.5 }}>
              ⚠️ An entry rule exists for this pair (status:{' '}
              <b>{anyRule.status}</b>). Hiding will stop market-data
              flow to the engine; any active rule will be inert until
              you re-expose the pair.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setHideDialogOpen(false)}
          size="small"
          disabled={hidePending}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            setHidePending(true);
            try {
              await onHidePair(pair);
              setHideDialogOpen(false);
            } catch {
              // The mutation's onError rolls the optimistic cache
              // back; we just keep the dialog open so the operator
              // can retry. Nothing else to do here.
            } finally {
              setHidePending(false);
            }
          }}
          variant="contained"
          color="error"
          size="small"
          disabled={hidePending}
        >
          {hidePending ? <CircularProgress size={14} /> : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Confirmation dialog with three-layer hard-cap guard. Also
        handles the path-switch warning when the operator picks a
        cross-path flag — `clearOthers` carries the old-path flags
        the PATCH will zero out atomically. */}
    <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '0.9rem', pb: 0.5 }}>
        Activate {confirmDialog ? FLAG_LABEL[confirmDialog.flag] : ''} maker (Path {confirmDialog?.targetPath.toUpperCase()})
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Pair:</b> {pair.pairName}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Entry path:</b>{' '}
            {confirmDialog?.targetPath === 'a'
              ? 'A — buy poly_yes + buy kalshi_no'
              : 'B — buy poly_no + buy kalshi_yes'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Maker leg:</b> {confirmDialog ? FLAG_LABEL[confirmDialog.flag] : ''}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Threshold sum:</b>{' '}
            {Number.isFinite(thresholdNum) ? thresholdNum.toFixed(3) : '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Order size:</b> {size || DEFAULT_SIZE} contracts
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Max volume:</b> ${maxVol || DEFAULT_MAX_VOL}/day
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Live path sum:</b> {fmtPrice(confirmTargetSum)}
          </Typography>
          {confirmDialog && confirmDialog.clearOthers.length > 0 && (
            <Box
              sx={{
                mt: 0.5,
                p: 0.8,
                borderRadius: 1,
                backgroundColor: 'rgba(255,152,0,0.12)',
                border: '1px solid rgba(255,152,0,0.4)',
                color: '#ffb74d',
              }}
            >
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 600 }}>
                Path switch: this will clear{' '}
                {confirmDialog.clearOthers
                  .map((f) => FLAG_LABEL[f])
                  .join(' + ')}{' '}
                before enabling the new path. Switch only when you are
                sure — the engine will start placing path-{confirmDialog.targetPath.toUpperCase()} orders.
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              mt: 0.5,
              p: 0.8,
              borderRadius: 1,
              backgroundColor: confirmOverHardCap ? 'rgba(244,67,54,0.18)' : 'rgba(76,175,80,0.10)',
              color: confirmOverHardCap ? '#f44336' : '#bdbdbd',
              border: confirmOverHardCap
                ? '1px solid rgba(244,67,54,0.4)'
                : '1px solid rgba(76,175,80,0.3)'
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
              Hard cap: $1.00. Threshold:{' '}
              {Number.isFinite(thresholdNum) ? `$${thresholdNum.toFixed(3)}` : '—'}
            </Typography>
            {confirmOverHardCap ? (
              <Typography sx={{ fontSize: '0.7rem', mt: 0.2 }}>
                Threshold is at or above the $1 hard cap — entry is blocked.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.7rem', mt: 0.2 }}>
                Maker rests at the limit price — the engine waits until
                maker_rest + counter_ask is under the cap before placing.
                Live path sum ({confirmTargetSum != null ? `$${confirmTargetSum.toFixed(3)}` : '—'})
                is informational.
              </Typography>
            )}
          </Box>
          {priceDriftedSinceOpen && !confirmOverHardCap && (
            <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', mt: 0.5 }}>
              Path sum changed by more than one tick since this dialog opened.
              Click Re-confirm to snap to the current price.
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', mt: 0.5 }}>
            This will start placing real BUY orders.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog(null)} size="small">
          Cancel
        </Button>
        {priceDriftedSinceOpen && !confirmOverHardCap ? (
          <Button onClick={handleReconfirm} variant="contained" color="warning" size="small">
            Re-confirm
          </Button>
        ) : (
          <Tooltip
            title={confirmOverHardCap ? 'Above $1 hard cap' : ''}
            placement="top"
            disableHoverListener={!confirmOverHardCap}
          >
            <span>
              <Button
                onClick={handleConfirmToggle}
                variant="contained"
                color="success"
                size="small"
                disabled={confirmOverHardCap}
              >
                Activate
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
}, (prev, next) => (
  prev.pair === next.pair
  && prev.rule === next.rule
  && prev.anyRule === next.anyRule
  && prev.liveState === next.liveState
  && prev.onCreateRule === next.onCreateRule
  && prev.onSetMakerFlag === next.onSetMakerFlag
  && prev.onUpdateRule === next.onUpdateRule
  && prev.onHidePair === next.onHidePair
));
